/**
 * Strict validation for a shadow moment's `start`/`end` window. Reviewers type
 * these by hand, and every consumer (clip cutting, seeking, Teaching Star
 * nominations) needs both bounds to be real times with the end after the start.
 */

/** `MM:SS` with an optional `.mmm` fraction; minutes may run past 59. */
const MINUTES_SECONDS_PATTERN = /^(\d{1,3}):(\d{2})(?:\.(\d{1,3}))?$/;

/** `H:MM:SS` with an optional `.mmm` fraction. */
const HOURS_MINUTES_SECONDS_PATTERN =
  /^(\d{1,2}):(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?$/;

/** Why a moment window is unusable. */
export type ShadowMomentWindowIssue =
  /** `start` is empty or not an `MM:SS` / `H:MM:SS` time. */
  | 'invalidStart'
  /** `end` is empty or not an `MM:SS` / `H:MM:SS` time. */
  | 'invalidEnd'
  /** Both bounds parse, but `end` is at or before `start`. */
  | 'endNotAfterStart';

/** A moment window resolved to offsets in seconds. */
export interface ShadowMomentWindowSeconds {
  /** Start offset from the part's start, in seconds. */
  startSeconds: number;
  /** End offset from the part's start, in seconds; always > `startSeconds`. */
  endSeconds: number;
}

/**
 * Parses one moment bound typed as `MM:SS` or `H:MM:SS`, each with an optional
 * `.mmm` fraction (so a VTT `HH:MM:SS.mmm` also parses). Seconds must be below
 * 60. In the two-part form minutes may be any count up to three digits, since
 * reviewers write `63:03` for a time past the first hour; in the three-part
 * form minutes must be below 60. No surrounding whitespace is allowed.
 *
 * @param value - The raw bound string.
 * @return The offset in seconds, or `undefined` when the string is not a valid
 *   time (empty, a bare number, `35.30`, `5:75`, `1:60:00`, and so on).
 * @throws Never.
 */
export function parseShadowMomentTime(value: string): number | undefined {
  const minutesSeconds = MINUTES_SECONDS_PATTERN.exec(value);
  if (minutesSeconds) {
    // An unmatched optional group is undefined at runtime, which the default
    // covers; the lib types every group as string.
    const [, minutesText, secondsText, fractionText = '0'] = minutesSeconds;
    return toSeconds({hours: 0, minutesText, secondsText, fractionText});
  }
  const hoursMinutesSeconds = HOURS_MINUTES_SECONDS_PATTERN.exec(value);
  if (!hoursMinutesSeconds) return undefined;
  const [, hoursText, minutesText, secondsText, fractionText = '0'] =
    hoursMinutesSeconds;
  if (Number(minutesText) >= 60) return undefined;
  return toSeconds({
    hours: Number(hoursText),
    minutesText,
    secondsText,
    fractionText,
  });
}

/**
 * Combines matched time parts into seconds, rejecting seconds of 60 or more.
 *
 * @param hours - Whole hours.
 * @param minutesText - Matched minutes digits.
 * @param secondsText - Matched two-digit seconds.
 * @param fractionText - Matched fraction digits (`'0'` when absent).
 * @return The offset in seconds, or `undefined` when seconds are out of range.
 * @throws Never.
 */
function toSeconds({
  hours,
  minutesText,
  secondsText,
  fractionText,
}: {
  hours: number;
  minutesText: string;
  secondsText: string;
  fractionText: string;
}): number | undefined {
  const seconds = Number(secondsText);
  if (seconds >= 60) return undefined;
  return (
    hours * 3600 +
    Number(minutesText) * 60 +
    seconds +
    Number(`0.${fractionText}`)
  );
}

/**
 * Reports what, if anything, is wrong with a moment window. The window is the
 * closed interval `[start, end]`, and it must be non-empty: `end` has to be
 * strictly after `start`, so `start === end` is an issue.
 *
 * @param start - The moment's raw start string.
 * @param end - The moment's raw end string.
 * @return The first issue found (start is checked before end), or `undefined`
 *   when the window is valid.
 * @throws Never.
 */
export function getShadowMomentWindowIssue({
  start,
  end,
}: {
  start: string;
  end: string;
}): ShadowMomentWindowIssue | undefined {
  const startSeconds = parseShadowMomentTime(start);
  if (startSeconds === undefined) return 'invalidStart';
  const endSeconds = parseShadowMomentTime(end);
  if (endSeconds === undefined) return 'invalidEnd';
  if (endSeconds <= startSeconds) return 'endNotAfterStart';
  return undefined;
}

/**
 * Resolves a moment window to seconds, rejecting anything
 * {@link getShadowMomentWindowIssue} reports. Never defaults or repairs a bound.
 *
 * @param start - The moment's raw start string.
 * @param end - The moment's raw end string.
 * @return The window in seconds with `endSeconds > startSeconds`, or
 *   `undefined` when either bound is invalid or the end is not after the start.
 * @throws Never.
 */
export function parseShadowMomentWindow({
  start,
  end,
}: {
  start: string;
  end: string;
}): ShadowMomentWindowSeconds | undefined {
  const startSeconds = parseShadowMomentTime(start);
  const endSeconds = parseShadowMomentTime(end);
  if (startSeconds === undefined || endSeconds === undefined) return undefined;
  if (endSeconds <= startSeconds) return undefined;
  return {startSeconds, endSeconds};
}

/**
 * True when a moment window is usable; see {@link getShadowMomentWindowIssue}
 * for the rules (closed interval, end strictly after start).
 *
 * @param start - The moment's raw start string.
 * @param end - The moment's raw end string.
 * @return Whether both bounds parse and the end is after the start.
 * @throws Never.
 */
export function isValidShadowMomentWindow({
  start,
  end,
}: {
  start: string;
  end: string;
}): boolean {
  return getShadowMomentWindowIssue({start, end}) === undefined;
}
